import React, { useEffect, useState } from "react";
import {
  Table,
  Input,
  Button,
  Space,
  Popconfirm,
  message,
  Tag,
  Tooltip,
  Image,
  Form,
} from "antd";

import startCase from "lodash/startCase";
import capitalize from "lodash/capitalize";
import { Typography } from "antd";
import {
  CalendarOutlined,
  DeleteOutlined,
  EditOutlined,
  UnorderedListOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import ScheduleForm from "../components/Schedules/ScheduleForm";
import axios from "axios";
import dayjs from "dayjs";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import RequirePermission from "../components/Common/RequirePermission";
const { Title } = Typography;

const Schedules = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [createPermission, setCreatePermission] = useState(false);
  const [editPermission, setEditPermission] = useState(false);
  const [deletePermission, setDeletePermission] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filterText, setFilterText] = useState("");

  const handleSearch = (e) => {
    const value = e.target.value;
    setFilterText(value);
    setCurrentPage(1);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    // if (typeof sorter.order === "undefined") {
    //   setSortingOrder("desc");
    //   setSortingColumn("id");
    // } else {
    //   setSortingColumn(sorter.field);
    //   setSortingOrder(sorter.order === "ascend" ? "asc" : "desc");
    // }
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const handleEdit = (record) => {
    setIsModalOpen(true);
    setEditingSchedule(record);
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_BASE_URL}/api/schedules/${id}`
      );
      const updated_row_count = response.data.data.updated_row_count;
      if (updated_row_count === 1) {
        message.success("Schedule deleted");
        await fetchData();
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleSync = async () => {
    try {
      setSyncLoading(true);
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/api/schedules/sync`
      );
      const updated_row_count = response.data.data.updated_row_count;
      if (updated_row_count === 1) {
        message.success("Schedule synced successfully");
        await fetchData();
      }
      else {
        message.info("No expired schedules found");
      }
      setSyncLoading(false);
    } catch (err) {
      console.log(err);
    }
  };
  
  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        process.env.REACT_APP_BASE_URL + `/api/schedules`,
        {
          params: {
            filter: filterText, // add filter if needed
            limit: pageSize,
            page: currentPage,
          },
        }
      );
      let records = response.data.data.data;
      setTotalRecords(response.data.data.total);
      setSchedules(
        records.map((schedule) => {
          return { ...schedule, showCode: false };
        })
      );
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching schedule data", error);
    }
  };

  useEffect(() => {
    const fetchTimer = setTimeout(() => {
      fetchData();
    }, 500);

    return () => clearTimeout(fetchTimer);
  }, [currentPage, pageSize, filterText]);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("userData"));
    setUser(storedUser);
    const permissions = storedUser.permissions;
    setCreatePermission(parseInt(permissions["signage.schedule_create"]) === 1);
    setDeletePermission(parseInt(permissions["signage.schedule_delete"]) === 1);
    setEditPermission(parseInt(permissions["signage.schedule_edit"]) === 1);
  }, []);

  const columns = [];

  columns.push({ title: "Schedule Name", dataIndex: "name" });

  columns.push({
    title: "Screens",
    dataIndex: "screen_name",
    render: (text) => {
      if (!text) return "No Screen Assigned";
      return text
        .split(",")
        .map((name, index) => <Tag key={index}>{capitalize(name.trim())}</Tag>);
    },
  });
  columns.push({
    title: "Status",
    dataIndex: "status",
    render: (status) => (
      <Tag color={status === "active" ? "green" : "red"}>
        {capitalize(status)}
      </Tag>
    ),
  });
  columns.push({
    title: "Updated At",
    dataIndex: "modified_at",
    render: (text) => (
      <span className="text-small">
        {dayjs(text).format("DD-MM-YYYY HH:mm")}
      </span>
    ),
  });
  columns.push({
    title: "Updated By",
    dataIndex: "updated_by_user",
    render: (text) => {
      return <span className="text-small">{startCase(text)}</span>;
    },
  });

  if (editPermission || deletePermission) {
    columns.push({
      title: "Actions",
      render: (_, record) => (
        <Space>
          {user && (
            <>
              {editPermission && (
                <>
                  <Button
                    size="small"
                    icon={<UnorderedListOutlined />}
                    title="Sequence"
                    onClick={() => navigate(`/sequences/${record.id}`)}
                  />
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(record)}
                  />
                </>
              )}
              {deletePermission && (
                <Popconfirm
                  title="Are you sure to delete this schedule?"
                  onConfirm={() => handleDelete(record.id)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button danger size="small" icon={<DeleteOutlined />} />
                </Popconfirm>
              )}
            </>
          )}
        </Space>
      ),
    });
  }

  return (
    <RequirePermission permission="signage.schedule_view">
      <div style={{ padding: "1rem" }}>
        <Helmet>
          <title>{`${process.env.REACT_APP_NAME} | Schedules`}</title>
        </Helmet>
        <Title level={3} style={{ marginBottom: "1rem" }}>
          Schedules
        </Title>
        <div className="d-flex justify-content-between mb-3">
          <div>
            <Form layout="inline">
              <Form.Item>
                <Input.Search
                  placeholder="Search schedule"
                  value={filterText}
                  className="me-1"
                  onChange={handleSearch}
                  style={{ width: 200 }}
                />
              </Form.Item>
            </Form>
          </div>

          {createPermission && (
            <div>
              <Button
                type="primary"
                onClick={() => {
                  setEditingSchedule(null);
                  setIsModalOpen(true);
                }}
                style={{marginRight: "1rem"}}
              >
                Add New Schedule
              </Button>
              <Button 
                icon={<SyncOutlined />} 
                onClick={() => handleSync()}
                loading={syncLoading}
                disabled={syncLoading}
                style={{backgroundColor: "#0b59c0ff", color: "white"}}
              >
                Sync
              </Button>
            </div>
          )}

        </div>

        <Table
          loading={loading}
          columns={columns}
          dataSource={schedules}
          scroll={{ x: "max-content" }}
          rowKey={(record) => record.id}
          onChange={handleTableChange}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalRecords,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} schedule`,
            showSizeChanger: true, // Enables the page size selection dropdown
            pageSizeOptions: ["5", "10", "20", "50", "100"], // Options for the page size selection
          }}
        />
        {isModalOpen && (
          <ScheduleForm
            isModalOpen={isModalOpen}
            closeModal={() => setIsModalOpen(false)}
            editingSchedule={editingSchedule}
            handleUpdateList={() => fetchData()}
          />
        )}
      </div>
    </RequirePermission>
  );
};

export default Schedules;
